import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { TrackerService } from './tracker.service';
import { TrackerCode } from '@models/tracker-event.model';

export enum NotificationType {
    SUCCESS = 'success',
    ERROR = 'error',
    WARNING = 'warning',
    INFO = 'info'
}

/**
 * Servicio centralizado para notificaciones y manejo de errores
 * Integra notificaciones visuales con tracking de eventos
 */
@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private defaultDuration = 5000;
    private errorDuration = 7000;

    constructor(
        private snackBar: MatSnackBar,
        private trackerService: TrackerService
    ) {}

    /**
     * Muestra una notificación de éxito
     */
    showSuccess(message: string, duration?: number): void {
        this.show(message, NotificationType.SUCCESS, duration || this.defaultDuration);
    }

    /**
     * Muestra una notificación de error y trackea el evento
     */
    showError(message: string, error?: any, context?: string, duration?: number): void {
        this.show(message, NotificationType.ERROR, duration || this.errorDuration);
        
        // Trackear el error
        if (error) {
            this.trackerService.trackError(
                TrackerCode.SYSTEM_ERROR,
                error,
                context || message
            );
        }
    }

    /**
     * Muestra una notificación de advertencia
     */
    showWarning(message: string, duration?: number): void {
        this.show(message, NotificationType.WARNING, duration || this.defaultDuration);
    }

    /**
     * Muestra una notificación informativa
     */
    showInfo(message: string, duration?: number): void {
        this.show(message, NotificationType.INFO, duration || this.defaultDuration);
    }

    /**
     * Maneja errores HTTP de manera consistente
     */
    handleHttpError(error: any, customMessage?: string, context?: string): void {
        let message = customMessage;
        
        if (!message) {
            if (error.status === 0) {
                message = 'Error de conexión. Por favor, verifica tu conexión a internet.';
                this.trackerService.trackError(TrackerCode.NETWORK_ERROR, error, context);
            } else if (error.status === 401) {
                message = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
                this.trackerService.trackError(TrackerCode.AUTH_TOKEN_EXPIRED, error, context);
            } else if (error.status === 403) {
                message = 'No tienes permisos para realizar esta acción.';
            } else if (error.status === 404) {
                message = 'Recurso no encontrado.';
            } else if (error.status === 408) {
                message = 'La solicitud ha excedido el tiempo de espera.';
                this.trackerService.trackError(TrackerCode.API_TIMEOUT, error, context);
            } else if (error.status >= 500) {
                message = 'Error del servidor. Por favor, intenta nuevamente.';
                this.trackerService.trackError(TrackerCode.API_ERROR, error, context);
            } else {
                message = error.error?.message || error.message || 'Ha ocurrido un error inesperado.';
                this.trackerService.trackError(TrackerCode.SYSTEM_ERROR, error, context);
            }
        } else {
            this.trackerService.trackError(TrackerCode.SYSTEM_ERROR, error, context);
        }

        this.showError(message || '', error, context || '');
    }

    /**
     * Muestra notificación genérica
     */
    private show(message: string, type: NotificationType, duration: number): void {
        const config: MatSnackBarConfig = {
            duration,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: [`snackbar-${type}`]
        };

        this.snackBar.open(message, 'Cerrar', config);
    }

    /**
     * Cierra todas las notificaciones activas
     */
    dismissAll(): void {
        this.snackBar.dismiss();
    }
}

